import React, { useEffect, useRef, useState } from "react";
import { ScanLine, ShieldCheck, ArrowRight, Sparkles, Check } from "lucide-react";
import { scanDemo, previewCopy } from "../scanMock";

const toneLabel = { high: "Avoid", med: "Caution", low: "Fine" };

const PhoneShell = ({ children, className = "", label, onClick, active }) => (
  <div
    className={`ph-wrap ${className} ${active ? "is-active" : ""}`}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
  >
    <div className="ph-frame">
      <div className="ph-notch" />
      <div className="ph-screen">{children}</div>
    </div>
    <p className="ph-label">{label}</p>
  </div>
);

const ScoreRing = ({ score, tone }) => {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <div className={`ph-ring tone-${tone}`}>
      <svg viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} className="ph-ring-bg" />
        <circle
          cx="32"
          cy="32"
          r={r}
          className="ph-ring-fg"
          strokeDasharray={c}
          strokeDashoffset={c - (c * score) / 100}
        />
      </svg>
      <span>{score}</span>
    </div>
  );
};

/* ---------- screen 1: scanning ---------- */
const ScanScreen = ({ product, phase }) => (
  <div className="ph-body ph-scan">
    <div className="ph-top">
      <span className="ph-chip">
        <ScanLine size={12} /> Scanning
      </span>
    </div>
    <div className="ph-viewfinder">
      <div className="ph-pack">
        <div className="ph-pack-cap" />
        <div className="ph-pack-label">
          <span>{product.name.split(" ")[0]}</span>
          <em>ingredients</em>
          <i />
          <i />
          <i className="short" />
        </div>
      </div>
      <span className="ph-corner tl" />
      <span className="ph-corner tr" />
      <span className="ph-corner bl" />
      <span className="ph-corner br" />
      <div className={`ph-laser ${phase === "scanning" ? "run" : ""}`} />
    </div>
    <p className="ph-barcode">{product.barcode}</p>
    <p className="ph-scan-note">
      {phase === "scanning" ? "Reading the label\u2026" : "Hold steady over the label"}
    </p>
  </div>
);

/* ---------- screen 2: result ---------- */
const ResultScreen = ({ product }) => (
  <div className="ph-body">
    <div className="ph-result-head">
      <ScoreRing score={product.score} tone={product.tone} />
      <div>
        <b>{product.name}</b>
        <span>{product.size}</span>
        <em className={`ph-verdict tone-${product.tone}`}>{product.verdict}</em>
      </div>
    </div>
    <div className="ph-flags">
      {product.flags.map((f) => (
        <div className={`ph-flag lvl-${f.level}`} key={f.name}>
          <div className="ph-flag-top">
            <b>{f.name}</b>
            <span className={`ph-pill lvl-${f.level}`}>{toneLabel[f.level]}</span>
          </div>
          <p>{f.why}</p>
          <small>
            <ShieldCheck size={10} /> {f.source}
          </small>
        </div>
      ))}
    </div>
  </div>
);

/* ---------- screen 3: swap ---------- */
const SwapScreen = ({ product }) => (
  <div className="ph-body">
    <div className="ph-top">
      <span className="ph-chip good">
        <Sparkles size={12} /> Cleaner swap
      </span>
    </div>
    <div className="ph-swap-row">
      <div className="ph-swap-from">
        <span>{product.score}</span>
        <p>{product.name}</p>
      </div>
      <ArrowRight size={16} className="ph-swap-arrow" />
      <div className="ph-swap-to">
        <span>{product.swap.score}</span>
        <p>{product.swap.name}</p>
      </div>
    </div>
    <p className="ph-swap-reason">{product.swap.reason}</p>
    <ul className="ph-wins">
      {product.swap.wins.map((w) => (
        <li key={w}>
          <Check size={12} /> {w}
        </li>
      ))}
    </ul>
    <div className="ph-cta-fake">Add to shopping list</div>
  </div>
);

const AppPreview = () => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | scanning | result
  const timers = useRef([]);

  useEffect(() => {
    const els = document.querySelectorAll(".preview-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(
    () => () => timers.current.forEach(clearTimeout),
    []
  );

  const product = scanDemo.products[index];

  const runDemo = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase("scanning");
    timers.current.push(setTimeout(() => setPhase("result"), 1700));
  };

  const pickProduct = (i) => {
    timers.current.forEach(clearTimeout);
    setIndex(i);
    setPhase("idle");
  };

  return (
    <section className="preview" id="preview">
      <p className="preview-eyebrow">{previewCopy.eyebrow}</p>
      <h2 className="preview-title">{previewCopy.title}</h2>
      <p className="preview-sub">{previewCopy.sub}</p>

      <div className="preview-picker" role="tablist" aria-label="Sample products">
        {scanDemo.products.map((p, i) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            className={`preview-tab ${i === index ? "is-on" : ""}`}
            onClick={() => pickProduct(i)}
          >
            {p.name}
          </button>
        ))}
      </div>

      <div className="preview-stage preview-reveal">
        <PhoneShell className="ph-side" label="1 · Scan and Discover">
          <ScanScreen product={product} phase="idle" />
        </PhoneShell>

        <PhoneShell
          className="ph-hero"
          label={
            phase === "result"
              ? "2 \u00b7 Compare and Choose"
              : "2 \u00b7 Tap to run a sample scan"
          }
          onClick={phase === "scanning" ? undefined : runDemo}
          active
        >
          {phase === "result" ? (
            <ResultScreen product={product} />
          ) : (
            <ScanScreen product={product} phase={phase} />
          )}
          {phase === "idle" && (
            <div className="ph-tap">
              <ScanLine size={16} />
              Tap to scan
            </div>
          )}
        </PhoneShell>

        <PhoneShell className="ph-side" label="3 · Track Your Progress">
          <SwapScreen product={product} />
        </PhoneShell>
      </div>

      <p className="preview-note">{previewCopy.note}</p>
    </section>
  );
};

export default AppPreview;
